import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import confetti from 'canvas-confetti';
import { Subscription } from 'rxjs';
import { CommonService } from 'src/app/Services/common.service';
import { UserSeat, WebinarRunDTO } from 'src/app/Services/WOWApi.Service';
import { CongratulationsPopupComponent, DynamicDialogConfig } from '../congratulations-popup/congratulations-popup.component';

interface DemoPhase {
  status: string;
  label: string;
  holdMs: number;
  selectedSeat: number;
}

const SAMPLE_VIDEO =
  'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerBlazes.mp4';
const SAMPLE_IMAGE = 'assets/Images/demo1.png';
const DEMO_SEAT_COUNT = 48;
/** What CalculateSeatsDisplayDelay returns for 48 seats: ceil(48/6) * 2 + 3. */
const DEMO_SEAT_DELAY_SEC = 19;
const DEMO_FIRST_SEAT_ID = 101;
const WINNER_SEAT = 41;
/** The API sends the winner as a seat id, so the harness has to do the same. */
const WINNER_SEAT_ID = DEMO_FIRST_SEAT_ID + WINNER_SEAT - 1;
/** Mirrors the function's range of NumberOfSeats * 1000 + 1. */
const DEMO_DRAW_MAX = DEMO_SEAT_COUNT * 1000 + 1;

const SEAT_NAMES = [
  'Alex Rivera', 'Sam Patel', 'Jordan Lee', 'Casey Nguyen',
  'Riley Brooks', 'Morgan Diaz', 'Taylor Quinn', 'Jamie Cole',
  'Avery Shah', 'Cameron Wu', 'Drew Bennett', 'Parker Singh',
  'Quinn Morales', 'Reese Okonkwo', 'Hayden Clarke', 'Skyler Amin',
  'Rowan Blake', 'Emery Cho', 'Finley Brooks', 'Sutton Rao',
  'Phoenix Alvarez', 'Eden Martel', 'Marlowe Chen', 'Shiloh Grant'
];

@Component({
  selector: 'app-webinar-run-demo',
  templateUrl: './webinar-run-demo.component.html',
  styleUrls: ['../Dashboard/webinar-run.component.css', './webinar-run-demo.component.css'],
  standalone: false
})
export class WebinarRunDemoComponent implements OnInit, OnDestroy {
  readonly phases: DemoPhase[] = [
    { status: 'Not Started', label: 'Countdown', holdMs: 20000, selectedSeat: 0 },
    { status: 'Video Playing', label: 'Video', holdMs: 12000, selectedSeat: 0 },
    { status: 'DisplaySeats', label: 'Seats', holdMs: DEMO_SEAT_DELAY_SEC * 1000, selectedSeat: 0 },
    { status: 'First Dummy Run', label: 'Check 1', holdMs: 10000, selectedSeat: 13947 },
    { status: 'Second Dummy Run', label: 'Check 2', holdMs: 10000, selectedSeat: 30682 },
    { status: 'Third Dummy Run', label: 'Check 3', holdMs: 10000, selectedSeat: 41215 },
    { status: 'Final Run', label: 'Winner', holdMs: 16000, selectedSeat: WINNER_SEAT_ID }
  ];

  dto: WebinarRunDTO;
  phaseIndex = 0;
  autoPlay = true;
  displaySeats = false;
  selectedSeat = '';
  isMobileView = false;
  seatIntroScrollDone = false;
  seatGridKey = 0;
  countdownRemaining = Number.POSITIVE_INFINITY;

  private didEnterDisplaySeatsPhase = false;
  private winnerRevealed = false;
  private viewSub: Subscription | null = null;
  private advanceHandle: ReturnType<typeof setTimeout> | null = null;
  private confettiHandle: ReturnType<typeof setInterval> | null = null;
  private readonly seats: UserSeat[] = Array.from({ length: DEMO_SEAT_COUNT }, (_, i) =>
    UserSeat.fromJS({
      seatId: 101 + i,
      seatNumber: i + 1,
      userName: SEAT_NAMES[i % SEAT_NAMES.length]
    })
  );

  constructor(
    private dialog: MatDialog,
    private commonService: CommonService
  ) {}

  ngOnInit(): void {
    this.viewSub = this.commonService.isMobileView.subscribe(res => this.isMobileView = res);
    this.goTo(0);
  }

  ngOnDestroy(): void {
    this.clearTimers();
    this.clearConfetti();
    this.viewSub?.unsubscribe();
  }

  get currentPhase(): DemoPhase {
    return this.phases[this.phaseIndex];
  }

  get runVideoUrl(): string {
    const v = this.dto?.video;
    return typeof v === 'string' && v.trim() ? v.trim() : '';
  }

  get isCountdownPhase(): boolean {
    const s = this.dto?.status;
    return s === 'Not Started' || s === 'Started';
  }

  get isVideoPlayingPhase(): boolean {
    return this.dto?.status === 'Video Playing';
  }

  get keepRunPlayer(): boolean {
    if (!this.runVideoUrl) {
      return false;
    }
    if (this.isVideoPlayingPhase) {
      return true;
    }
    return this.isCountdownPhase && this.countdownRemaining <= 45;
  }

  /** Mirrors WebinarRunComponent: only the official draw marks a seat as drawn. */
  get winnerSeatNumber(): number {
    const dto = this.dto;
    if (!dto || ['Final Run', 'Closed', 'Completed'].indexOf(dto.status) === -1) {
      return 0;
    }
    if (dto.status !== 'Completed' && !this.winnerRevealed) {
      return 0;
    }
    const selected = Number(dto.selectedSeat);
    if (!Number.isFinite(selected) || selected <= 0) {
      return 0;
    }
    const seats = dto.userSeats || [];
    const match = seats.find(s => s.seatId === selected) || seats.find(s => s.seatNumber === selected);
    return match ? match.seatNumber : 0;
  }

  goTo(index: number): void {
    if (index < 0 || index >= this.phases.length) {
      return;
    }
    this.clearTimers();
    this.phaseIndex = index;
    const phase = this.phases[index];

    if (phase.status === 'Not Started') {
      this.displaySeats = false;
      this.didEnterDisplaySeatsPhase = false;
      this.seatIntroScrollDone = false;
      this.winnerRevealed = false;
      this.selectedSeat = '';
      this.seatGridKey++;
      this.countdownRemaining = Number.POSITIVE_INFINITY;
    }

    if (phase.status === 'DisplaySeats') {
      this.didEnterDisplaySeatsPhase = true;
      this.seatIntroScrollDone = false;
      this.displaySeats = true;
    }

    const dummyOrFinal = [
      'First Dummy Run', 'Second Dummy Run', 'Third Dummy Run', 'Final Run'
    ];
    if (dummyOrFinal.indexOf(phase.status) !== -1) {
      this.displaySeats = true;
    }

    this.dto = this.buildDto(phase);

    if (this.autoPlay && phase.holdMs > 0 && index < this.phases.length - 1) {
      this.advanceHandle = setTimeout(() => this.next(), phase.holdMs);
    }
  }

  next(): void {
    if (this.phaseIndex < this.phases.length - 1) {
      this.goTo(this.phaseIndex + 1);
    }
  }

  back(): void {
    if (this.phaseIndex > 0) {
      this.goTo(this.phaseIndex - 1);
    }
  }

  restart(): void {
    this.goTo(0);
  }

  toggleAutoPlay(): void {
    this.autoPlay = !this.autoPlay;
    if (this.autoPlay) {
      this.goTo(this.phaseIndex);
    } else {
      this.clearAdvance();
    }
  }

  shouldShowResultsPanel(): boolean {
    if (!this.dto || !this.displaySeats) {
      return false;
    }
    if (this.dto.status === 'DisplaySeats') {
      return false;
    }
    if (!this.didEnterDisplaySeatsPhase) {
      return true;
    }
    return this.seatIntroScrollDone;
  }

  onSeatIntroScrollComplete(): void {
    this.seatIntroScrollDone = true;
  }

  openCongratsPopup(info: DynamicDialogConfig): void {
    this.winnerRevealed = true;
    this.selectedSeat = info.SeatNumber != null ? info.SeatNumber.toString() : '';
    this.scrollToWinnerSeat(this.selectedSeat);
    setTimeout(() => {
      this.dialog.open(CongratulationsPopupComponent, {
        width: '600px',
        data: info,
        panelClass: 'winner-dialog-panel',
        backdropClass: 'winner-dialog-backdrop'
      });
      this.blastParty();
    }, 1200);
  }

  private buildDto(phase: DemoPhase): WebinarRunDTO {
    return WebinarRunDTO.fromJS({
      status: phase.status,
      webinarStartDateTime: new Date(Date.now() + phase.holdMs).toISOString(),
      currentDateTime: new Date().toISOString(),
      selectedSeat: phase.selectedSeat,
      fileExtention: '.mp4',
      video: SAMPLE_VIDEO,
      userSeats: this.seats,
      secondsToStart: phase.status === 'Not Started' ? Math.ceil(phase.holdMs / 1000) : 0,
      firstDummyRunMin: 1,
      firstDummyRunMax: DEMO_DRAW_MAX,
      secondDummyRunMin: 1,
      secondDummyRunMax: DEMO_DRAW_MAX,
      thirdDummyRunMin: 1,
      thirdDummyRunMax: DEMO_DRAW_MAX,
      seatsDisplayDelaySeconds: DEMO_SEAT_DELAY_SEC,
      itemImageUrl: SAMPLE_IMAGE,
      webinarName: 'Demo Webinar — UI harness'
    });
  }

  private scrollToWinnerSeat(seatNumber: string): void {
    if (!seatNumber) {
      return;
    }
    setTimeout(() => {
      const winnerEl = document.getElementById('seat' + seatNumber);
      winnerEl?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
    }, 0);
  }

  private blastParty(): void {
    const duration = 15 * 1000;
    const animationEnd = Date.now() + duration;
    // The app raises .cdk-overlay-container to 10500, so the confetti has to clear that
    // to rain in front of the winner card.
    const zIndex = 10600;
    const colors = ['#ffce1b', '#ff9d2e', '#ffffff', '#c41e3a', '#8b7cff'];
    const randomInRange = (min: number, max: number) => Math.random() * (max - min) + min;

    confetti({ particleCount: 140, angle: 60, spread: 70, startVelocity: 55, origin: { x: 0, y: 1 }, colors, zIndex });
    confetti({ particleCount: 140, angle: 120, spread: 70, startVelocity: 55, origin: { x: 1, y: 1 }, colors, zIndex });

    const defaults = { startVelocity: 30, spread: 360, ticks: 60, colors, zIndex };
    this.clearConfetti();
    this.confettiHandle = setInterval(() => {
      const timeLeft = animationEnd - Date.now();
      if (timeLeft <= 0) {
        this.clearConfetti();
        return;
      }
      const particleCount = 50 * (timeLeft / duration);
      confetti({ ...defaults, particleCount, origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 } });
      confetti({ ...defaults, particleCount, origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 } });
    }, 250);
  }

  private clearConfetti(): void {
    if (this.confettiHandle != null) {
      clearInterval(this.confettiHandle);
      this.confettiHandle = null;
    }
  }

  private clearAdvance(): void {
    if (this.advanceHandle != null) {
      clearTimeout(this.advanceHandle);
      this.advanceHandle = null;
    }
  }

  private clearTimers(): void {
    this.clearAdvance();
  }
}
